// Use the api.js module which has both proxy-based and direct API instances
import api, { directApi } from './api';
// Keep importing getBackendBaseUrl for redirect URLs
import { getBackendBaseUrl } from '../utils/configUtils';

// Choose the appropriate API instance based on environment
// Use proxy-based api for development, directApi only when REACT_APP_API_URL is set
const apiInstance = process.env.REACT_APP_API_URL ? directApi : api;

/**
 * Service for authentication-related API operations
 * Optimized for mobile web usage
 */
const authService = {
  /**
   * Handle OAuth callback and get token
   * @param {string} code - Authorization code from Google OAuth
   * @returns {Promise} Promise object with token data
   */
  handleAuthCallback: async (code) => {
    try {
      const response = await apiInstance.get(`/auth/callback?code=${code}`);
      return response.data;
    } catch (error) {
      console.error('Error handling auth callback:', error);
      throw error;
    }
  },

  /**
   * Get current user profile
   * @returns {Promise} Promise object with user data
   */
  getCurrentUser: async () => {
    try {
      const response = await apiInstance.get('/users/me');
      
      // Convert backend field names to frontend field names
      const userData = response.data;
      
      // Map full_name to name for frontend consistency
      if (userData && userData.full_name !== undefined) {
        userData.name = userData.full_name;
      }
      
      console.log('User data after field normalization:', userData);
      return userData;
    } catch (error) {
      console.error('Error fetching current user:', error);
      throw error;
    }
  },

  /**
   * Update user profile
   * @param {Object} userData - User data to update
   * @returns {Promise} Promise object with updated user data
   */
  updateUserProfile: async (userData) => {
    try {
      console.log('Profile update requested with data:', userData);
      
      // Make sure we're sending a properly formatted update object
      const updateData = {};
      
      // Debug the exact field structure coming from the frontend
      console.log('Profile update BEFORE processing input fields:');
      console.log('- Name:', userData.name, typeof userData.name);
      console.log('- Name trimmed:', userData.name?.trim());
      
      // Only include fields that have actual values - with correct field names for API
      if (userData.name && userData.name.trim()) {
        // CRITICAL: Backend expects 'full_name' not 'name'
        updateData.full_name = userData.name.trim();
        console.log(`Setting full_name to "${updateData.full_name}"`);
      } else {
        console.log('No valid name provided for update');
      }
      
      // Email updates are not supported in the backend API
      console.log('Note: Email updates are not supported by the backend API');
      
      // Password updates if present
      if (userData.password) {
        updateData.password = userData.password;
        console.log('Password update included in request');
      }
      
      // Prevent empty update requests
      if (Object.keys(updateData).length === 0) {
        console.error('No valid fields to update');
        throw new Error('No valid fields to update. Please provide at least one field to change.');
      }
      
      console.log('Sending profile update with FINAL payload:', JSON.stringify(updateData));
      const response = await apiInstance.put('/users/me', updateData);
      
      console.log('Profile update response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error updating user profile:', error);
      throw error;
    }
  },

  /**
   * Perform email and password login
   * @param {Object} credentials - User credentials
   * @returns {Promise} Promise object with token data
   */
  login: async (credentials) => {
    try {
      const response = await apiInstance.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  },

  /**
   * Perform registration with email and password
   * @param {Object} userData - User registration data
   * @returns {Promise} Promise object with token data
   */
  register: async (userData) => {
    try {
      // Map 'name' to 'full_name' for backend consistency
      const backendData = { ...userData };
      if (backendData.name !== undefined) {
        backendData.full_name = backendData.name;
        delete backendData.name; // Remove name to avoid confusion
      }
      
      console.log('Sending registration data:', backendData);
      const response = await apiInstance.post('/auth/register', backendData);
      return response.data;
    } catch (error) {
      console.error('Error during registration:', error);
      throw error;
    }
  },

  /**
   * Get Google OAuth login URL
   * @returns {string} Google OAuth login URL
   */
  getGoogleLoginUrl: () => {
    // For OAuth redirect flows, we need to use the full backend URL
    // The proxy in package.json only works for API requests, not redirects
    const backendBaseUrl = getBackendBaseUrl();
    const relativeUrl = '/api/v2/auth/login/google';
    
    // Check if there's a returnPath stored (e.g., from family invitation)
    const returnPath = sessionStorage.getItem('returnPath');
    
    // Include the current app base path in the redirect URL
    // This tells the backend where to redirect after successful OAuth
    const currentOrigin = window.location.origin;
    
    let redirectUrl;
    if (returnPath) {
      // If there's a returnPath, use it as the redirect destination
      redirectUrl = `${currentOrigin}${returnPath}`;
      console.log('[authService] Using returnPath for OAuth redirect:', returnPath);
    } else {
      // Otherwise, redirect to the app home
      const basePath = process.env.REACT_APP_BASE_PATH || '/app';
      // Ensure we don't create double slashes
      const normalizedPath = basePath === '/' ? '/' : `${basePath}/`;
      redirectUrl = `${currentOrigin}${normalizedPath}`;
    }
    
    return `${backendBaseUrl}${relativeUrl}?redirect_url=${encodeURIComponent(redirectUrl)}`;
  },

  /**
   * Get Apple Sign In login URL
   * @returns {string} Apple Sign In login URL
   */
  getAppleLoginUrl: () => {
    // For OAuth redirect flows, we need to use the full backend URL
    // The proxy in package.json only works for API requests, not redirects
    const backendBaseUrl = getBackendBaseUrl();
    const relativeUrl = '/api/v2/auth/login/apple';
    
    // Check if there's a returnPath stored (e.g., from family invitation)
    const returnPath = sessionStorage.getItem('returnPath');
    
    // Include the current app base path in the redirect URL
    // This tells the backend where to redirect after successful OAuth
    const currentOrigin = window.location.origin;
    
    let redirectUrl;
    if (returnPath) {
      // If there's a returnPath, use it as the redirect destination
      redirectUrl = `${currentOrigin}${returnPath}`;
      console.log('[authService] Using returnPath for Apple OAuth redirect:', returnPath);
    } else {
      // Otherwise, redirect to the app home
      const basePath = process.env.REACT_APP_BASE_PATH || '/app';
      // Ensure we don't create double slashes
      const normalizedPath = basePath === '/' ? '/' : `${basePath}/`;
      redirectUrl = `${currentOrigin}${normalizedPath}`;
    }
    
    const fullUrl = `${backendBaseUrl}${relativeUrl}?redirect_url=${encodeURIComponent(redirectUrl)}`;
    console.log('[authService] Apple login URL:', fullUrl);
    return fullUrl;
  },

  /**
   * Store authentication token
   * @param {string} token - JWT token
   */
  setToken: (token) => {
    localStorage.setItem('cubxly_token', token);
  },

  /**
   * Get stored authentication token
   * @returns {string|null} JWT token or null if not found
   */
  getToken: () => {
    return localStorage.getItem('cubxly_token');
  },

  /**
   * Remove stored authentication token
   */
  removeToken: () => {
    localStorage.removeItem('cubxly_token');
  },

  /**
   * Check if user is authenticated
   * @returns {boolean} true if authenticated, false otherwise
   */
  isAuthenticated: () => {
    return !!localStorage.getItem('cubxly_token');
  },

  /**
   * Perform logout
   */
  logout: () => {
    localStorage.removeItem('cubxly_token');
  },

  /**
   * Request password reset
   * @param {string} email - User email address
   * @returns {Promise} Promise object with response data
   */
  requestPasswordReset: async (email) => {
    try {
      const response = await apiInstance.post('/auth/forgot-password', { email });
      return {
        success: true,
        message: response.data.message,
        data: response.data
      };
    } catch (error) {
      console.error('Error requesting password reset:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to request password reset'
      };
    }
  },

  /**
   * Validate password reset token
   * @param {string} token - Reset token
   * @returns {Promise} Promise object with validation data
   */
  validateResetToken: async (token) => {
    try {
      const response = await apiInstance.get(`/auth/reset-password/${token}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error validating reset token:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to validate reset token'
      };
    }
  },

  /**
   * Reset password with token
   * @param {string} token - Reset token
   * @param {string} password - New password
   * @returns {Promise} Promise object with response data
   */
  resetPassword: async (token, password) => {
    try {
      const response = await apiInstance.post('/auth/reset-password', {
        token,
        password
      });
      return {
        success: true,
        message: response.data.message,
        data: response.data
      };
    } catch (error) {
      console.error('Error resetting password:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to reset password'
      };
    }
  },

  /**
   * Send email verification
   * @param {string} email - User email address
   * @returns {Promise} Promise object with response data
   */
  sendVerificationEmail: async (email) => {
    try {
      const response = await apiInstance.post('/auth/send-verification-email', { email });
      return {
        success: true,
        message: response.data.message,
        data: response.data
      };
    } catch (error) {
      console.error('Error sending verification email:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to send verification email'
      };
    }
  },

  /**
   * Validate email verification token
   * @param {string} token - Verification token
   * @returns {Promise} Promise object with validation data
   */
  validateVerificationToken: async (token) => {
    try {
      const response = await apiInstance.get(`/auth/verify-email/${token}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Error validating verification token:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to validate verification token'
      };
    }
  },

  /**
   * Verify email with token
   * @param {string} token - Verification token
   * @returns {Promise} Promise object with response data
   */
  verifyEmail: async (token) => {
    try {
      const response = await apiInstance.post('/auth/verify-email', { token });
      return {
        success: true,
        message: response.data.message,
        data: response.data
      };
    } catch (error) {
      console.error('Error verifying email:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to verify email'
      };
    }
  },

  /**
   * Resend verification email
   * @param {string} email - User's email address
   * @returns {Promise} Promise object with response data
   */
  resendVerificationEmail: async (email) => {
    try {
      const response = await apiInstance.post('/auth/resend-verification', { email });
      return {
        success: true,
        message: response.data.message,
        data: response.data
      };
    } catch (error) {
      console.error('Error resending verification email:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to resend verification email'
      };
    }
  },

  /**
   * Delete user account and all associated data (GDPR/Facebook compliance)
   * WARNING: This action is IRREVERSIBLE
   * @returns {Promise} Promise object with deletion result
   */
  deleteAccount: async () => {
    try {
      const response = await apiInstance.delete('/users/me/delete', {
        data: { confirm_deletion: true }
      });
      return {
        success: true,
        message: response.data.message,
        stats: response.data.stats
      };
    } catch (error) {
      console.error('Error deleting account:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to delete account'
      };
    }
  }
};

export default authService;
